/**
 * Единый реестр внутренних путей приложения.
 * Роутер (routes.tsx) и вся навигация (useNavigate, Link, href) берут адреса
 * отсюда — строковые литералы путей в коде страниц и компонентов запрещены.
 */
export const PATHS = {
    landing: "/",
    about: "/about",
    register: "/register",
    /** Визард регистрации для входа через VK ID / Яндекс ID — без шага email/пароля (#1279). */
    registerComplete: "/register/complete",
    resetPassword: "/reset-password",

    app: "/app",
    /** Лента с открытым окном подписки (тарифы). Сюда ведут push и письма о
     *  подписке — backend/app/core/billing_notifications.py SUBSCRIPTION_URL. */
    appSubscription: "/app?open=subscription",
    appChats: "/app/chats",
    /** Прямая ссылка на «Куда сходить» изнутри /app (issue #676 п.11).
     * Раньше такого маршрута не было — прямой заход по ссылке улетал в catch-all
     * и отдавал пустой редирект на /app. */
    appWhereToGo: "/app/where-to-go",
    paymentConfirm: "/payment/confirm",
    support: "/support",
    /** Легаси-адрес редактирования профиля — редиректит в приложение. */
    profileEdit: "/profile/edit",

    admin: "/admin",

    company: {
        login: "/company/login",
        register: "/company/register",
        dashboard: "/company/dashboard",
        placesNew: "/company/places/new",
        placesEditPattern: "/company/places/:id/edit",
        placesEditTo: (id: string) => `/company/places/${id}/edit`,
        placementStatusPattern: "/company/placements/:id",
        placementStatus: (id: string) => `/company/placements/${id}`,
    },

    privacy: "/privacy",
    terms: "/terms",
    offer: "/offer",
    consentPd: "/consent-pd",
    consentMailing: "/consent-mailing",
    functionalCharacteristics: "/functional-characteristics",
    installationOperation: "/installation-operation",
    userGuide: "/user-guide",

    whereToGo: "/where-to-go",
    /**
     * «Куда сходить» с конкретным собеседником. Тот же экран и тот же список мест,
     * что и общий вход с главной, — персонализируется только шапка, поэтому это
     * query к существующему адресу, а не отдельный маршрут.
     *
     * До этого три точки входа (MatchModal в ленте, стек мэтчей, карточка
     * лайкнувшего) собирали этот адрес строковым литералом прямо в Swipes.tsx —
     * мимо реестра, из-за чего пол собеседника терялся и шапка выдавала «с Игорь».
     */
    whereToGoWith: (
        name: string,
        avatar?: string,
        gender?: "male" | "female",
        chat?: {conversationId?: string; peerUserId?: string},
    ) =>
        `/where-to-go?name=${encodeURIComponent(name)}`
        + (avatar ? `&avatar=${encodeURIComponent(avatar)}` : "")
        // Пол нужен только склонению имени в шапке и только там, где слово само
        // за себя не отвечает («с Кармен» не склоняется, «с Иваном» — да).
        + (gender ? `&gender=${gender}` : "")
        + (chat?.conversationId ? `&conversationId=${encodeURIComponent(chat.conversationId)}` : "")
        + (chat?.peerUserId ? `&peerUserId=${encodeURIComponent(chat.peerUserId)}` : ""),

    /**
     * Страница разбора по прямой ссылке (issue #1066). Сама страница живёт поверх
     * экрана, с которого её открыли (адрес `?analysis=<id>` на том же пути), —
     * этот маршрут только приводит прямую ссылку к ленте с открытой страницей.
     * view: "details" — сразу подробный разбор.
     */
    appAnalysis: {
        pattern: "/app/analysis/:userId",
        to: (userId: string, view?: "details") => `/app/analysis/${encodeURIComponent(userId)}${view ? `?view=${view}` : ""}`,
        /** Куда ведёт прямая ссылка: лента с открытой страницей разбора. */
        inFeed: (userId: string, view?: string | null) =>
            `/app?analysis=${encodeURIComponent(userId)}${view ? `&view=${encodeURIComponent(view)}` : ""}`,
    },
    appProfile: "/app/profile",
    /** «Приобретённые ИИ-разборы» — отдельная страница списка под профилем. */
    appAnalysisHistory: "/app/profile/analyses",

    scan: {
        pattern: "/scan/:userId",
        to: (userId: string) => `/scan/${userId}`,
    },
    invite: {
        pattern: "/invite/:inviterId",
        to: (inviterId: string) => `/invite/${inviterId}`,
    },

    /** Лендинг с открытой модалкой авторизации; returnTo — куда вернуть после входа. */
    landingAuth: (mode: "login" | "register", returnTo?: string) =>
        `/?auth=${mode}${returnTo ? `&return=${encodeURIComponent(returnTo)}` : ""}`,
} as const;
